import {
  AgentStreamHttpError,
  AgentStreamPrematureCloseError,
  streamAgentChat,
  type AgentTokenClaims,
} from "@/clients/streaming/agent-stream";
import { AgentTurnsService, type AgentTurnRecord } from "@/clients/streaming/agent-turns";
import type {
  AgentFrame,
  AgentFramePlanFoodsCommitted,
  AgentFrameStatus,
} from "@/clients/streaming/agent-types";
import { ApiError } from "@/clients/core/ApiError";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import { QueryKeys } from "@/constants/query-keys";
import {
  chatMessagesAtom,
  chatOriginAtom,
  chatSessionIdAtom,
  pendingChatImageAtom,
  pendingChatTurnAtom,
  type ChatMessage,
  type ChatMessageImage,
  type ChatMessageInterrupt,
  type ChatMessagePlanFoodsCommitted,
  type ChatMessagePresentation,
  type PendingChatTurn,
} from "@/stores/chat.store";
import {
  addPlanFoodBadges,
  badgesFromCommittedFrame,
  committedCardId,
  committedMealIds,
  pendingFoodAddAtom,
  planFoodBadgesAtom,
  prependMealIds,
  recentlyAddedMealIdsAtom,
} from "@/stores/plan-food-add.store";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { captureException } from "@sentry/react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@clerk/clerk-react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

export type {
  ChatMessage,
  ChatMessageRole,
  ChatMessageImage,
  ChatMessageInterrupt,
  ChatMessagePlanFoodsCommitted,
  ChatMessagePresentation,
} from "@/stores/chat.store";

const createMessageId = () => crypto.randomUUID();

const ERROR_BUBBLE_TEXT = "Something went wrong. Please try again.";

export type AgentErrorDisposition = "reconcile" | "cancelled" | "failure";

export const classifyAgentErrorCode = (code: string): AgentErrorDisposition => {
  if (code === "turn_in_progress" || code === "session_busy") return "reconcile";
  if (code === "turn_cancelled") return "cancelled";
  return "failure";
};

export const isPostTokenStatusBoundary = (
  hasEmittedToken: boolean,
  frame: AgentFrameStatus,
): boolean => hasEmittedToken && frame.type === "status";

const SENTRY_BODY_PREVIEW_LIMIT = 500;

const tokenAgeMs = (claims?: AgentTokenClaims): number | undefined =>
  claims?.iat !== undefined ? Date.now() - claims.iat * 1000 : undefined;

const msUntilExp = (claims?: AgentTokenClaims): number | undefined =>
  claims?.exp !== undefined ? claims.exp * 1000 - Date.now() : undefined;

const reportChatErrorToSentry = (err: unknown, ctx: { sessionId: string; turnIndex: number }) => {
  const exception = err instanceof Error ? err : new Error(String(err));
  const httpStatus = err instanceof AgentStreamHttpError ? err.status : undefined;
  const claims =
    err instanceof AgentStreamHttpError || err instanceof AgentStreamPrematureCloseError
      ? err.tokenClaims
      : undefined;
  const bodyPreview =
    err instanceof AgentStreamHttpError
      ? err.bodyText?.slice(0, SENTRY_BODY_PREVIEW_LIMIT)
      : undefined;

  const agentErrorCategory =
    err instanceof AgentStreamHttpError ? err.agentErrorCategory : undefined;

  captureException(exception, {
    tags: {
      feature: "ai-chat",
      phase: "stream",
      errorName: exception.name,
      ...(httpStatus !== undefined ? { httpStatus: String(httpStatus) } : {}),
      ...(agentErrorCategory ? { agentErrorCategory } : {}),
    },
    // `agentClaims` and `agentRequestAgeMs` deliberately avoid the substring
    // "token" — Sentry's default `@password:filter` wipes any extra whose key
    // matches `*token*`, which previously erased these diagnostics entirely.
    extra: {
      sessionId: ctx.sessionId,
      turnIndex: ctx.turnIndex,
      agentErrorMessage: err instanceof AgentStreamHttpError ? err.agentErrorMessage : undefined,
      responseBodyPreview: bodyPreview,
      agentClaims: claims,
      agentRequestAgeMs: tokenAgeMs(claims),
      msUntilExp: msUntilExp(claims),
      didRetry: err instanceof AgentStreamHttpError ? err.didRetry : undefined,
    },
  });
};

// Per-frame drain queue tuning. SSE tokens arrive in bursts (Bedrock buffers
// chunks before flushing); rendering them as they arrive looks janky. We
// queue incoming text and release it on requestAnimationFrame at a steady
// rate. No catch-up acceleration — buffer drains at a constant pace
// regardless of how full it gets (local stream detachment still clears it instantly).
const TARGET_CHARS_PER_SECOND = 12;

const prevAgentSegmentCount = (messages: ChatMessage[], clientTurnId: string): number =>
  messages.filter((message) => message.role === "agent" && message.clientTurnId === clientTurnId)
    .length;

/** Resolves once the active plan/balance queries have refetched, so callers
 *  can hold transient UI (the My Plan "Adding food..." card) until the new
 *  data is actually on screen. */
const invalidatePlanQueries = (queryClient: QueryClient): Promise<void> =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [QueryKeys.PLAN] }),
    queryClient.invalidateQueries({ queryKey: [QueryKeys.BALANCE_CALORIES] }),
    queryClient.invalidateQueries({ queryKey: [QueryKeys.BALANCE_PROTEIN] }),
    queryClient.invalidateQueries({ queryKey: [QueryKeys.CALORIE_BUDGET] }),
  ]).then(() => undefined);

const PROFILE_QUERY_KEYS = [
  QueryKeys.USER,
  QueryKeys.USER_STATS,
  QueryKeys.BODY_STATS,
  QueryKeys.ACTIVITY_LEVEL,
  QueryKeys.MEDICAL,
  QueryKeys.GOALS,
  QueryKeys.NUTRITIONAL_REQUIREMENT,
  QueryKeys.DIETARY_REQUIREMENT,
  QueryKeys.MEAL_PREFERENCES,
  QueryKeys.FAVORITE_CUISINES,
  QueryKeys.LIFESTYLE,
  QueryKeys.OTHER_PREFERENCES,
  QueryKeys.SUPERMARKET_PREFERENCES,
] as const;

const invalidateProfileQueries = (queryClient: QueryClient) => {
  PROFILE_QUERY_KEYS.forEach((queryKey) => {
    queryClient.invalidateQueries({ queryKey: [queryKey] });
  });
};

interface CanonicalTextSegment {
  kind: "text";
  text: string;
  presentation?: ChatMessagePresentation;
}

interface CanonicalCommittedCard {
  kind: "plan-foods-committed";
  committed: ChatMessagePlanFoodsCommitted;
}

type CanonicalAgentEntry = CanonicalTextSegment | CanonicalCommittedCard;

const frameNeedsPlanRefresh = (frame: AgentFramePlanFoodsCommitted): boolean =>
  frame.days.some(
    (day) => day.remaining_energy_kcal === null || day.remaining_protein_grams === null,
  );

const canonicalAgentEntries = (record: AgentTurnRecord): CanonicalAgentEntry[] => {
  const entries: CanonicalAgentEntry[] = [];
  const seenBasketIds = new Set<string>();
  let activeText = "";
  let activeSource: "token" | "text" | null = null;
  let activePresentation: ChatMessagePresentation | undefined;
  const flush = () => {
    if (!activeText) return;
    entries.push({ kind: "text", text: activeText, presentation: activePresentation });
    activeText = "";
    activeSource = null;
    activePresentation = undefined;
  };

  record.frames.forEach((frame) => {
    if (frame.type === "token") {
      if (activeSource === "text") flush();
      const framePresentation = frame.kind === "background_note" ? "background-note" : undefined;
      // Recovery records omit transient status frames, so a change in token
      // presentation is itself a bubble boundary in either direction.
      if (activeText && activePresentation !== framePresentation) flush();
      activeSource = "token";
      activePresentation = framePresentation;
      activeText += frame.text;
    } else if (frame.type === "text") {
      flush();
      activeSource = "text";
      activeText = frame.text;
    } else if (frame.type === "plan_foods_committed") {
      flush();
      if (frame.schema_version !== 1 || seenBasketIds.has(frame.basket_id)) return;
      seenBasketIds.add(frame.basket_id);
      frame.days.forEach((day) =>
        entries.push({
          kind: "plan-foods-committed",
          committed: {
            schemaVersion: frame.schema_version,
            basketId: frame.basket_id,
            day,
          },
        }),
      );
    } else if (frame.type === "status") {
      // Every post-token status is a structural bubble boundary, regardless
      // of whether its beats are empty. Presentation comes only from token
      // metadata; welcome and ordinary reply tokens stay unstyled.
      flush();
    } else if (["done", "interrupt", "error"].includes(frame.type)) {
      flush();
    }
  });
  flush();
  return entries;
};

const canonicalTextSegments = (record: AgentTurnRecord): CanonicalTextSegment[] =>
  canonicalAgentEntries(record).filter(
    (entry): entry is CanonicalTextSegment => entry.kind === "text",
  );

const recordInterrupt = (record: AgentTurnRecord): ChatMessageInterrupt | null => {
  if (record.interrupt) return record.interrupt;
  const frame = record.frames.find((candidate) => candidate.type === "interrupt");
  if (!frame || frame.type !== "interrupt") return null;
  return { question: frame.question, payload: frame.payload, editable: frame.editable };
};

const recordErrorText = (record: AgentTurnRecord): string => {
  const frame = record.frames.find((candidate) => candidate.type === "error");
  return frame?.type === "error" && frame.message ? frame.message : ERROR_BUBBLE_TEXT;
};

export const mergeCanonicalTurnMessages = (
  previous: ChatMessage[],
  record: AgentTurnRecord,
): ChatMessage[] => {
  // The background note is transient progress UI. A terminal replay replaces
  // the partial turn wholesale, so only durable welcome/final-answer segments
  // belong in the recovered transcript.
  const entries = canonicalAgentEntries(record).filter(
    (entry) => entry.kind !== "text" || entry.presentation !== "background-note",
  );
  const interrupt = recordInterrupt(record);
  const withoutPartialResponse = previous.filter(
    (message) =>
      message.role === "user" ||
      (message.clientTurnId !== record.client_turn_id && message.turnId !== record.turn_id),
  );
  const blockedBasketIds = new Set(
    withoutPartialResponse.flatMap((message) =>
      message.planFoodsCommitted ? [message.planFoodsCommitted.basketId] : [],
    ),
  );
  const terminalMessages: ChatMessage[] = [];

  entries.forEach((entry) => {
    if (entry.kind === "plan-foods-committed" && blockedBasketIds.has(entry.committed.basketId)) {
      return;
    }
    const segmentIndex = terminalMessages.length;
    if (entry.kind === "text") {
      terminalMessages.push({
        id: `${record.turn_id}:${segmentIndex}`,
        role: "agent",
        text: entry.text,
        turnId: record.turn_id,
        clientTurnId: record.client_turn_id,
        segmentIndex,
        presentation: entry.presentation,
      });
      return;
    }
    terminalMessages.push({
      id: committedCardId(entry.committed.basketId, entry.committed.day.target_date),
      role: "agent",
      text: "",
      planFoodsCommitted: entry.committed,
      turnId: record.turn_id,
      clientTurnId: record.client_turn_id,
      segmentIndex,
    });
  });

  if (record.status === "completed" && terminalMessages.length > 0) {
    terminalMessages[terminalMessages.length - 1].traceId = record.trace_id;
  }

  if (record.status === "interrupted" && interrupt) {
    terminalMessages.push({
      id: `${record.turn_id}:interrupt`,
      role: "agent",
      text: interrupt.question,
      interrupt,
      turnId: record.turn_id,
      clientTurnId: record.client_turn_id,
      segmentIndex: terminalMessages.length,
    });
  } else if (record.status === "failed") {
    terminalMessages.push({
      id: `${record.turn_id}:error`,
      role: "error",
      text: recordErrorText(record),
      turnId: record.turn_id,
      clientTurnId: record.client_turn_id,
    });
  } else if (record.status === "cancelled") {
    terminalMessages.push({
      id: `${record.turn_id}:cancelled`,
      role: "error",
      text: "Response stopped.",
      turnId: record.turn_id,
      clientTurnId: record.client_turn_id,
    });
  }

  return [...withoutPartialResponse, ...terminalMessages];
};

const useChatSessionController = () => {
  const sessionId = useAtomValue(chatSessionIdAtom);
  const chatOrigin = useAtomValue(chatOriginAtom);
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn } = useAuth();
  const [messages, setMessages] = useAtom(chatMessagesAtom);
  const [pendingImages, setPendingImages] = useAtom(pendingChatImageAtom);
  const [pendingTurn, setPendingTurn] = useAtom(pendingChatTurnAtom);
  const setPendingFoodAdd = useSetAtom(pendingFoodAddAtom);
  const setRecentlyAddedMealIds = useSetAtom(recentlyAddedMealIdsAtom);
  const setPlanFoodBadges = useSetAtom(planFoodBadgesAtom);
  const pendingTurnRef = useRef(pendingTurn);
  pendingTurnRef.current = pendingTurn;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const [isStreaming, setIsStreaming] = useState(false);
  const [showTypingDots, setShowTypingDots] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const detachedStreamRef = useRef<AbortController | null>(null);

  // Always detach the local reader if the app-level provider unmounts.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const cancelLocalStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    detachedStreamRef.current = null;
    setIsStreaming(false);
    setShowTypingDots(false);
  }, []);

  const detachLocalReader = useCallback(() => {
    if (!abortRef.current) return;
    detachedStreamRef.current = abortRef.current;
    setIsStreaming(false);
    setShowTypingDots(false);
  }, []);

  // Streams a single agent turn for an already-appended user message.
  // Shared by sendPrompt (fresh send) and retryLastError (re-run after a
  // failure) so the streaming/drain/terminal logic lives in one place.
  const runTurn = useCallback(
    async (
      prompt: string,
      images: ChatMessageImage[],
      turnIndex: number,
      turn: { clientTurnId: string; userMessageId: string; startedAt: string },
      trackSend = true,
      messageKind: "new" | "retry" = "new",
    ): Promise<boolean> => {
      const trimmed = prompt.trim();

      if (trackSend) {
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_MESSAGE_SENT, {
          message_kind: messageKind,
          message_id: turn.userMessageId,
          chat_origin: chatOrigin ?? "unknown",
          prompt_length: trimmed.length,
          turn_index: turnIndex,
          has_image: images.length > 0,
          image_count: images.length,
        });
      }

      const controller = new AbortController();
      abortRef.current = controller;
      detachedStreamRef.current = null;
      const isDetached = () => detachedStreamRef.current === controller;
      setIsStreaming(true);
      setShowTypingDots(true);

      const startedAt = performance.now();
      let firstTokenAt: number | null = null;
      // True once any token has streamed this turn (across all phases). Gates
      // the "seal the bubble on a status frame" behaviour so it only fires
      // *after* text has appeared — a cold-open welcome followed by a status
      // frame. A status frame before any token (every normal turn) is left
      // untouched. Distinct from `firstTokenAt`, which stays turn-level for
      // analytics and must not reset across phases.
      let hasEmittedToken = false;
      // Whether the thinking dot is currently on screen
      // for the active phase. A status frame raises it; the next token lowers
      // it. Tracked per-phase so we clear the UI exactly once when a reply
      // starts — not on every token and not only on the turn's very first
      // token (which would leave the post-welcome dot stuck on screen).
      let showingThinking = true;
      let assistantId: string | null = null;
      let lastDurableAgentId: string | null = null;
      let assistantText = "";
      let assistantPresentation: ChatMessagePresentation | undefined;
      let hasBackgroundNote = false;
      let pendingText = "";
      let rafId: number | null = null;
      let lastTickAt: number | null = null;
      let terminalType:
        | "done"
        | "interrupt"
        | "error"
        | "cancelled"
        | "reconcile"
        | "premature_close"
        | null = null;
      let errorCode: string | null = null;
      // Langfuse trace id from the `done` SSE frame. Stamped on the
      // streamed assistant message once the visible drain has finished so
      // the thumb up/down actions can post feedback tied to this turn.
      // null when Langfuse fails to mint a trace.
      let doneTraceId: string | null = null;
      let serverTurnId: string | undefined = pendingTurnRef.current?.turnId;
      // Deferred terminal payloads — added to the message list only after the
      // visible drain has caught up, so the user actually sees the slow stream
      // of the lead-up text before the closing bubble appears.
      // Trailing `as` keeps the declared type wide so TS doesn't collapse
      // these variables to their `null` literal initializer — the only
      // mutations happen inside the handleFrame closure, which TS doesn't
      // follow through. Without the cast, the truthy checks below narrow
      // to `never` and lookups like `.question` fail to type-check.
      let pendingInterruptFrame = null as {
        question: string;
        payload: Record<string, unknown>;
        editable?: boolean;
      } | null;
      let pendingErrorMessage = null as string | null;
      // Plan refetch kicked off by this turn's plan_updated / commit frame.
      // The My Plan "Adding food..." card waits on it so the placeholder is
      // swapped for the real meal card with no blank gap in between.
      let planRefresh = null as Promise<void> | null;
      let hasPendingFoodAdd = false;
      // While a background add is running, a `plan_updated` frame arrives as
      // soon as the tool finishes, but the `plan_foods_committed` frame (the
      // meal ids we hoist and badge) only comes with the first reply token,
      // seconds later. Refetching on `plan_updated` would show the new card
      // in its default slot first and move it to the top later. So the
      // refetch is held until the committed frame (or the end of the turn).
      let deferredPlanRefresh = false;
      const clearPendingFoodAdd = () =>
        setPendingFoodAdd((current) =>
          current?.clientTurnId === turn.clientTurnId ? null : current,
        );
      const seenBasketIds = new Set(
        messagesRef.current.flatMap((message) =>
          message.planFoodsCommitted ? [message.planFoodsCommitted.basketId] : [],
        ),
      );

      const commitChunk = (chunk: string) => {
        if (!assistantId) {
          const id = createMessageId();
          const presentation = assistantPresentation;
          assistantId = id;
          if (presentation !== "background-note") lastDurableAgentId = id;
          assistantText = chunk;
          const segmentIndex = prevAgentSegmentCount(messagesRef.current, turn.clientTurnId);
          setMessages((prev) => [
            ...prev,
            {
              id,
              role: "agent",
              text: chunk,
              clientTurnId: turn.clientTurnId,
              turnId: serverTurnId,
              segmentIndex,
              presentation,
            },
          ]);
        } else {
          assistantText += chunk;
          const id = assistantId;
          setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: assistantText } : m)));
        }
      };

      const drain = (now: number) => {
        rafId = null;
        if (controller.signal.aborted || isDetached()) {
          pendingText = "";
          lastTickAt = null;
          return;
        }
        if (lastTickAt === null) lastTickAt = now;
        const elapsed = now - lastTickAt;
        lastTickAt = now;

        const release = Math.max(1, Math.floor((elapsed * TARGET_CHARS_PER_SECOND) / 1000));
        const chunk = pendingText.slice(0, release);
        pendingText = pendingText.slice(release);
        if (chunk) commitChunk(chunk);

        if (pendingText.length > 0) {
          rafId = requestAnimationFrame(drain);
        } else {
          lastTickAt = null;
        }
      };

      const enqueueText = (text: string) => {
        pendingText += text;
        if (rafId === null && !controller.signal.aborted && !isDetached()) {
          rafId = requestAnimationFrame(drain);
        }
      };

      const removeBackgroundNote = () => {
        if (!hasBackgroundNote) return;
        hasBackgroundNote = false;
        setMessages((prev) =>
          prev.filter(
            (message) =>
              message.clientTurnId !== turn.clientTurnId ||
              message.presentation !== "background-note",
          ),
        );
      };

      const waitForDrain = () =>
        new Promise<void>((resolve) => {
          const tick = () => {
            if (controller.signal.aborted || isDetached() || pendingText.length === 0) {
              resolve();
              return;
            }
            requestAnimationFrame(tick);
          };
          tick();
        });

      const dropBuffer = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        pendingText = "";
        lastTickAt = null;
      };

      const handleFrame = async (frame: AgentFrame) => {
        switch (frame.type) {
          case "start":
            // Connection opened. Keep dots until first token.
            if (frame.turn_id) {
              serverTurnId = frame.turn_id;
              setPendingTurn((current) =>
                current?.clientTurnId === turn.clientTurnId
                  ? { ...current, turnId: frame.turn_id, status: "running" }
                  : current,
              );
              setPendingImages([]);
            }
            return;
          case "status": {
            // A status frame that lands *after* text has already streamed
            // (the cold-open welcome bubble) marks a new thinking phase: the
            // agent has finished the welcome and is about to work before its
            // real reply. Seal the current bubble so the next token/interrupt
            // opens a fresh one and continue showing the thinking dot. Before
            // any token this is the normal pre-answer case. (A failed welcome
            // generator ships no token, so the status arrives first.)
            const isBubbleBoundary = isPostTokenStatusBoundary(hasEmittedToken, frame);
            if (isBubbleBoundary) {
              // Let the welcome text finish its visible drain before we seal
              // the bubble — otherwise the un-drained tail would spill into
              // the fresh bubble the next token opens. Safe to block the frame
              // loop: a cold-open status is followed by ~7s of silence.
              await waitForDrain();
              if (controller.signal.aborted || isDetached()) return;
              assistantId = null;
              assistantText = "";
              assistantPresentation = undefined;
              setShowTypingDots(true);
            }
            // Status copy is intentionally ignored. The existing typing dot
            // remains the sole indicator until the next token arrives.
            showingThinking = true;
            return;
          }
          case "token": {
            if (firstTokenAt === null) {
              firstTokenAt = performance.now();
              trackMixpanelEvent(MixpanelEvents.AI_CHAT_FIRST_TOKEN, {
                ms_to_first_token: Math.round(firstTokenAt - startedAt),
                turn_index: turnIndex,
              });
            }
            hasEmittedToken = true;
            if (frame.kind === "background_note") {
              hasBackgroundNote = true;
              if (!hasPendingFoodAdd) {
                hasPendingFoodAdd = true;
                setPendingFoodAdd({
                  clientTurnId: turn.clientTurnId,
                  startedAt: new Date().toISOString(),
                });
              }
              const presentation: ChatMessagePresentation = "background-note";
              assistantPresentation = presentation;
              if (assistantId !== null) {
                const noteId = assistantId;
                setMessages((prev) =>
                  prev.map((message) =>
                    message.id === noteId ? { ...message, presentation } : message,
                  ),
                );
              }
            } else if (hasBackgroundNote && assistantPresentation !== "background-note") {
              // The first ordinary token after the tagged note is the durable
              // answer. Remove the transient note before opening that bubble.
              removeBackgroundNote();
            }
            // The answer (or welcome) is streaming — dismiss the thinking UI
            // for this phase. Guarded so we clear exactly once per phase: a
            // post-welcome status raises `showingThinking` again, and its
            // reply's first token clears it here a second time.
            if (showingThinking) {
              setShowTypingDots(false);
              showingThinking = false;
            }
            enqueueText(frame.text);
            return;
          }
          case "tool_start":
            // Keep the dot only while we're still in the pre-token "thinking"
            // phase. Once text has begun streaming, a tool starting must not
            // pop the dot back up alongside the visible text.
            if (firstTokenAt === null) setShowTypingDots(true);
            return;
          case "tool_end":
            return;
          case "plan_foods_committed": {
            if (frame.schema_version !== 1 || seenBasketIds.has(frame.basket_id)) return;
            seenBasketIds.add(frame.basket_id);
            setRecentlyAddedMealIds((prev) => prependMealIds(prev, committedMealIds(frame)));

            // A background note is transient progress UI, so do not make the
            // user wait for its typewriter buffer before replacing it with the
            // authoritative confirmation. Ordinary text, however, must finish
            // visibly before the card is inserted to preserve wire order.
            if (hasBackgroundNote) {
              dropBuffer();
              removeBackgroundNote();
            } else {
              await waitForDrain();
            }
            if (controller.signal.aborted || isDetached()) return;

            assistantId = null;
            assistantText = "";
            assistantPresentation = undefined;
            // The deterministic card proves the write, but the turn may still
            // continue with an assumption disclosure or other assistant copy.
            // Restore the thinking marker until the next token arrives (or
            // the terminal frame closes the stream) so the card does not read
            // like the conversation has already finished.
            setShowTypingDots(true);
            showingThinking = true;

            const cardIds = frame.days.map((day) =>
              committedCardId(frame.basket_id, day.target_date),
            );
            lastDurableAgentId = cardIds.at(-1) ?? lastDurableAgentId;
            setMessages((prev) => {
              if (
                prev.some((message) => message.planFoodsCommitted?.basketId === frame.basket_id)
              ) {
                return prev;
              }
              const segmentStart = prevAgentSegmentCount(prev, turn.clientTurnId);
              const cards: ChatMessage[] = frame.days.map((day, index) => ({
                id: cardIds[index],
                role: "agent",
                text: "",
                planFoodsCommitted: {
                  schemaVersion: frame.schema_version,
                  basketId: frame.basket_id,
                  day,
                },
                clientTurnId: turn.clientTurnId,
                turnId: serverTurnId,
                segmentIndex: segmentStart + index,
              }));
              return [...prev, ...cards];
            });
            // Only after the cards are in the transcript: the chat page prunes
            // any badge whose message it cannot find, so writing the badge
            // earlier (before the note removal above re-renders the list)
            // would get it deleted straight away.
            setPlanFoodBadges((prev) =>
              addPlanFoodBadges(prev, badgesFromCommittedFrame(frame, sessionId)),
            );
            if (hasPendingFoodAdd || deferredPlanRefresh || frameNeedsPlanRefresh(frame)) {
              deferredPlanRefresh = false;
              planRefresh = invalidatePlanQueries(queryClient);
              // The confirmation is authoritative: swap the "Adding food..."
              // card for the real one as soon as the refetched plan lands,
              // without waiting for the rest of the reply.
              if (hasPendingFoodAdd) planRefresh.then(clearPendingFoodAdd, clearPendingFoodAdd);
            }
            return;
          }
          case "plan_updated":
            if (hasPendingFoodAdd) {
              deferredPlanRefresh = true;
              return;
            }
            planRefresh = invalidatePlanQueries(queryClient);
            return;
          case "profile_updated":
            invalidateProfileQueries(queryClient);
            return;
          case "text":
            // Full text frames are used by reconciliation, not live SSE, but
            // accepting one here keeps staged backend variants compatible.
            if (showingThinking) {
              setShowTypingDots(false);
              showingThinking = false;
            }
            enqueueText(frame.text);
            return;
          case "interrupt": {
            removeBackgroundNote();
            // Defer the interrupt bubble until the lead-up text has finished
            // streaming visibly. Added after waitForDrain() below.
            pendingInterruptFrame = {
              question: frame.question,
              payload: frame.payload,
              editable: frame.editable,
            };
            terminalType = "interrupt";
            return;
          }
          case "done":
            removeBackgroundNote();
            doneTraceId = frame.trace_id;
            terminalType = "done";
            return;
          case "error":
            removeBackgroundNote();
            errorCode = frame.code;
            switch (classifyAgentErrorCode(frame.code)) {
              case "reconcile":
                // An idempotent retry can reconnect while its original turn
                // (or a cross-device turn on this session) is still running.
                // Keep the durable record and switch to /latest polling.
                terminalType = "reconcile";
                return;
              case "cancelled":
                pendingErrorMessage = "Response stopped.";
                terminalType = "cancelled";
                return;
              case "failure":
                pendingErrorMessage = frame.message || ERROR_BUBBLE_TEXT;
                terminalType = "error";
                return;
            }
        }
      };

      try {
        for await (const frame of streamAgentChat({
          prompt: trimmed,
          sessionId,
          clientTurnId: turn.clientTurnId,
          images,
          signal: controller.signal,
        })) {
          if (isDetached()) break;
          await handleFrame(frame);
        }
        // Stream finished. Let the visible drain finish before showing the
        // closing bubble so the slow text rendering doesn't get clobbered.
        await waitForDrain();
        if (controller.signal.aborted || isDetached()) return false;
        // Stamp the Langfuse trace id from the `done` frame on the
        // streamed agent message so its thumb up/down can post feedback
        // tied to this turn. doneTraceId is null when Langfuse hiccupped
        // — we still write it so the bubble can disable the thumbs UI.
        if (terminalType === "done" && lastDurableAgentId !== null) {
          const id = lastDurableAgentId;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === id
                ? {
                    ...m,
                    traceId: doneTraceId,
                    turnId: serverTurnId,
                    clientTurnId: turn.clientTurnId,
                  }
                : m,
            ),
          );
        }
        if (pendingInterruptFrame) {
          const frame = pendingInterruptFrame;
          const interrupt: ChatMessageInterrupt = {
            question: frame.question,
            payload: frame.payload,
            editable: frame.editable,
          };
          setMessages((prev) => [
            ...prev,
            {
              id: createMessageId(),
              role: "agent",
              text: frame.question,
              interrupt,
              clientTurnId: turn.clientTurnId,
              turnId: serverTurnId,
              segmentIndex: prevAgentSegmentCount(prev, turn.clientTurnId),
            },
          ]);
        } else if (pendingErrorMessage !== null) {
          setMessages((prev) => [
            ...prev,
            {
              id: createMessageId(),
              role: "error",
              text: pendingErrorMessage as string,
              clientTurnId: turn.clientTurnId,
              turnId: serverTurnId,
            },
          ]);
        }
      } catch (err) {
        if (controller.signal.aborted || isDetached()) {
          // The local reader was detached (background, canonical reconcile or
          // explicit server cancellation). The durable turn remains pending
          // unless the authoritative cancellation/result path clears it.
          dropBuffer();
          return false;
        }
        if (!(err instanceof AgentStreamHttpError)) {
          // Do not wait for the typewriter buffer: requestAnimationFrame is
          // suspended in the background and would delay resume reconciliation.
          setPendingTurn((current) =>
            current?.clientTurnId === turn.clientTurnId
              ? { ...current, turnId: serverTurnId, status: "reconciling" }
              : current,
          );
          dropBuffer();
          return false;
        }
        // Let already-streamed text finish rendering before the error bubble
        // appears underneath. Skip if the user has cancelled in the meantime.
        await waitForDrain();
        if (controller.signal.aborted || isDetached()) {
          dropBuffer();
          return false;
        }
        const status = err.status;
        errorCode = status ? `http_${status}` : "client_error";
        reportChatErrorToSentry(err, { sessionId, turnIndex });
        setMessages((prev) => [
          ...prev,
          {
            id: createMessageId(),
            role: "error",
            text: ERROR_BUBBLE_TEXT,
            clientTurnId: turn.clientTurnId,
            turnId: serverTurnId,
          },
        ]);
        terminalType = "error";
      } finally {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        if (abortRef.current === controller) abortRef.current = null;
        if (detachedStreamRef.current === controller) detachedStreamRef.current = null;
        setIsStreaming(false);
        setShowTypingDots(false);
      }

      if (controller.signal.aborted || isDetached()) return false;

      if (terminalType === "reconcile") {
        setPendingTurn((current) =>
          current?.clientTurnId === turn.clientTurnId
            ? { ...current, turnId: serverTurnId, status: "reconciling" }
            : current,
        );
        return false;
      }

      if (terminalType === "error") {
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_RESPONSE_FAILED, {
          error_code: errorCode ?? "unknown",
          turn_index: turnIndex,
        });
      } else if (terminalType !== "cancelled") {
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_RESPONSE_RECEIVED, {
          response_length: assistantText.length,
          had_image: images.length > 0,
          image_count: images.length,
          terminal_type: terminalType ?? "premature_close",
          ms_to_first_token: firstTokenAt !== null ? Math.round(firstTokenAt - startedAt) : -1,
          turn_index: turnIndex,
        });
      }

      if (["done", "interrupt", "error", "cancelled"].includes(terminalType ?? "")) {
        setPendingTurn((current) => (current?.clientTurnId === turn.clientTurnId ? null : current));
        setPendingImages([]);
        if (hasPendingFoodAdd) {
          // A held refetch whose committed frame never came still runs now.
          if (deferredPlanRefresh) planRefresh = invalidatePlanQueries(queryClient);
          // On success keep the "Adding food..." card until the refetched plan
          // is on screen; on any other ending there is nothing to wait for.
          const refresh = ["done"].includes(terminalType ?? "") ? planRefresh : null;
          (refresh ?? Promise.resolve()).then(clearPendingFoodAdd, clearPendingFoodAdd);
        }
      }

      return ["done", "interrupt"].includes(terminalType ?? "");
    },
    [
      chatOrigin,
      queryClient,
      sessionId,
      setMessages,
      setPendingImages,
      setPendingTurn,
      setPendingFoodAdd,
      setRecentlyAddedMealIds,
      setPlanFoodBadges,
    ],
  );

  const sendPrompt = useCallback(
    async (prompt: string, images: ChatMessageImage[] = [], agentPromptOverride?: string) => {
      const trimmed = prompt.trim();
      if ((!trimmed && images.length === 0) || isStreaming || pendingTurn) return false;

      const userMessage: ChatMessage = {
        id: createMessageId(),
        role: "user",
        text: trimmed,
        images,
      };
      const clientTurnId = crypto.randomUUID();
      userMessage.clientTurnId = clientTurnId;
      setMessages((prev) => [...prev, userMessage]);

      // The visible bubble shows the user's clean text; the agent can receive
      // an augmented prompt (e.g. with meal context prepended) via the
      // override so the transcript stays clean.
      const agentPrompt = agentPromptOverride?.trim() ? agentPromptOverride : trimmed;
      const startedAt = new Date().toISOString();
      setPendingTurn({
        sessionId,
        clientTurnId,
        userMessageId: userMessage.id,
        status: "starting",
        startedAt,
        agentPrompt,
        imageCount: images.length,
      });

      // Count user turns *before* this message — messagesRef hasn't picked up
      // the append above yet, so this is the 0-indexed turn of the new message.
      const turnIndex = messagesRef.current.filter((m) => m.role === "user").length;
      return runTurn(agentPrompt, images, turnIndex, {
        clientTurnId,
        userMessageId: userMessage.id,
        startedAt,
      });
    },
    [isStreaming, pendingTurn, runTurn, sessionId, setMessages, setPendingTurn],
  );

  // Re-run the last user turn after an error bubble. Drops the failed agent
  // partial + error bubble that trailed the last user message, then streams
  // the same prompt/image again without appending a duplicate user message.
  const retryLastError = useCallback(() => {
    if (isStreaming || pendingTurn) return;
    const msgs = messagesRef.current;
    let lastUserIdx = -1;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === "user") {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx === -1) return;

    const userMsg = msgs[lastUserIdx];
    const turnIndex = msgs.slice(0, lastUserIdx).filter((m) => m.role === "user").length;
    const clientTurnId = crypto.randomUUID();
    const userMessageId = userMsg.id;
    const startedAt = new Date().toISOString();
    const retriedUserMessage = { ...userMsg, clientTurnId };
    setMessages([...msgs.slice(0, lastUserIdx), retriedUserMessage]);
    setPendingTurn({
      sessionId,
      clientTurnId,
      userMessageId,
      status: "starting",
      startedAt,
      agentPrompt: userMsg.text,
      imageCount: userMsg.images?.length ?? 0,
    });
    void runTurn(
      userMsg.text,
      userMsg.images ?? [],
      turnIndex,
      {
        clientTurnId,
        userMessageId,
        startedAt,
      },
      true,
      "retry",
    );
  }, [isStreaming, pendingTurn, runTurn, sessionId, setMessages, setPendingTurn]);

  const applyTurnRecord = useCallback(
    (record: AgentTurnRecord): boolean => {
      const current = pendingTurnRef.current;
      if (!current || current.clientTurnId !== record.client_turn_id) return false;

      if (record.status === "running") {
        setPendingTurn({ ...current, turnId: record.turn_id, status: "running" });
        setPendingImages([]);
        setShowTypingDots(true);
        return false;
      }

      const segments = canonicalTextSegments(record);
      setMessages((previous) => mergeCanonicalTurnMessages(previous, record));

      const committedFrames = record.frames.filter(
        (frame): frame is AgentFramePlanFoodsCommitted => frame.type === "plan_foods_committed",
      );
      if (committedFrames.length > 0) {
        setRecentlyAddedMealIds((prev) =>
          prependMealIds(prev, committedFrames.flatMap(committedMealIds)),
        );
        const badgeSessionId = current.sessionId;
        setPlanFoodBadges((prev) =>
          addPlanFoodBadges(
            prev,
            committedFrames.flatMap((frame) => badgesFromCommittedFrame(frame, badgeSessionId)),
          ),
        );
      }
      const needsPlanRefresh =
        record.plan_updated ||
        record.frames.some((frame) => frame.type === "plan_updated") ||
        committedFrames.some(frameNeedsPlanRefresh);
      const planRefresh = needsPlanRefresh ? invalidatePlanQueries(queryClient) : null;
      // Hide the My Plan "Adding food..." card once the refreshed plan (if any)
      // has landed. The note itself was dropped by mergeCanonicalTurnMessages.
      const clearPendingFoodAdd = () =>
        setPendingFoodAdd((current) =>
          current?.clientTurnId === record.client_turn_id ? null : current,
        );
      (planRefresh ?? Promise.resolve()).then(clearPendingFoodAdd, clearPendingFoodAdd);
      if (
        record.profile_updated ||
        record.frames.some((frame) => frame.type === "profile_updated")
      ) {
        invalidateProfileQueries(queryClient);
      }

      if (record.status === "failed") {
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_RESPONSE_FAILED, {
          error_code: "reconciled_failure",
        });
      } else if (record.status !== "cancelled") {
        trackMixpanelEvent(MixpanelEvents.AI_CHAT_RESPONSE_RECEIVED, {
          response_length: segments.reduce((total, segment) => total + segment.text.length, 0),
          terminal_type: record.status,
          resumed: true,
        });
      }

      cancelLocalStream();
      setPendingImages([]);
      setPendingTurn((latest) => (latest?.clientTurnId === record.client_turn_id ? null : latest));
      return true;
    },
    [
      cancelLocalStream,
      queryClient,
      setMessages,
      setPendingImages,
      setPendingTurn,
      setPendingFoodAdd,
      setRecentlyAddedMealIds,
      setPlanFoodBadges,
    ],
  );

  const recoveryInFlightRef = useRef(false);
  const reconcilePendingTurn = useCallback(async (): Promise<void> => {
    const current = pendingTurnRef.current;
    if (!current || !isLoaded || !isSignedIn || recoveryInFlightRef.current || isStreaming) return;
    recoveryInFlightRef.current = true;
    setPendingTurn((latest) =>
      latest?.clientTurnId === current.clientTurnId ? { ...latest, status: "reconciling" } : latest,
    );
    try {
      const record = await AgentTurnsService.getLatestAgentTurn(current.sessionId);
      if (record.client_turn_id === current.clientTurnId) {
        applyTurnRecord(record);
        return;
      }

      if (record.status === "running") {
        // A turn started elsewhere is holding the session lock. Do not spam
        // retries that can only produce session_busy; wait for it to finish,
        // then retry this logical send with its original idempotency key.
        setPendingTurn((latest) =>
          latest?.clientTurnId === current.clientTurnId
            ? { ...latest, status: "reconciling" }
            : latest,
        );
        setShowTypingDots(true);
        return;
      }

      // `latest` can briefly still refer to the previous terminal turn. Re-post
      // the exact logical send with the same idempotency key; the backend will
      // return the existing turn if it had already accepted it.
      const userTurnIndex = messagesRef.current
        .slice(
          0,
          messagesRef.current.findIndex((message) => message.id === current.userMessageId),
        )
        .filter((message) => message.role === "user").length;
      void runTurn(
        current.agentPrompt,
        pendingImages.slice(0, current.imageCount),
        Math.max(0, userTurnIndex),
        {
          clientTurnId: current.clientTurnId,
          userMessageId: current.userMessageId,
          startedAt: current.startedAt,
        },
        false,
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404 && !current.turnId) {
        // No start acknowledgement means the POST may never have arrived.
        // Retry the same logical send with the same client_turn_id; server
        // idempotency prevents a duplicate if it was accepted after all.
        const userTurnIndex = messagesRef.current
          .slice(
            0,
            messagesRef.current.findIndex((message) => message.id === current.userMessageId),
          )
          .filter((message) => message.role === "user").length;
        void runTurn(
          current.agentPrompt,
          pendingImages.slice(0, current.imageCount),
          Math.max(0, userTurnIndex),
          {
            clientTurnId: current.clientTurnId,
            userMessageId: current.userMessageId,
            startedAt: current.startedAt,
          },
          false,
        );
      } else {
        // Offline and transient main-API failures are retried by the polling,
        // foreground and online listeners. They are not agent failures.
        setPendingTurn((latest) =>
          latest?.clientTurnId === current.clientTurnId
            ? { ...latest, status: "reconciling" }
            : latest,
        );
      }
    } finally {
      recoveryInFlightRef.current = false;
    }
  }, [applyTurnRecord, isLoaded, isSignedIn, isStreaming, pendingImages, runTurn, setPendingTurn]);

  const pendingClientTurnId = pendingTurn?.clientTurnId;

  useEffect(() => {
    if (!pendingClientTurnId || isStreaming || !isLoaded || !isSignedIn || !navigator.onLine)
      return;
    void reconcilePendingTurn();
    const intervalId = window.setInterval(() => void reconcilePendingTurn(), 2000);
    return () => window.clearInterval(intervalId);
  }, [isLoaded, isSignedIn, isStreaming, pendingClientTurnId, reconcilePendingTurn]);

  useEffect(() => {
    const handleOnline = () => void reconcilePendingTurn();
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        // Stop consuming locally without aborting the fetch or calling the
        // cancel endpoint; the backend continues the durable turn.
        detachLocalReader();
      } else {
        void reconcilePendingTurn();
      }
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    if (Capacitor.getPlatform() === "web") {
      return () => {
        window.removeEventListener("online", handleOnline);
        document.removeEventListener("visibilitychange", handleVisibility);
      };
    }

    const listener = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void reconcilePendingTurn();
      } else {
        detachLocalReader();
      }
    });
    return () => {
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      void listener.then((handle) => handle.remove());
    };
  }, [detachLocalReader, reconcilePendingTurn]);

  const commitAcceptedCancellation = useCallback(
    (turn: PendingChatTurn) => {
      // Cancel suppresses output but cannot roll back tools that already ran.
      // Conservatively refresh both domains because the acknowledgement does
      // not carry the final turn's plan/profile flags.
      applyTurnRecord({
        turn_id: turn.turnId ?? `cancelled:${turn.clientTurnId}`,
        client_turn_id: turn.clientTurnId,
        status: "cancelled",
        started_at: turn.startedAt,
        completed_at: new Date().toISOString(),
        frames: [],
        plan_updated: true,
        profile_updated: true,
        interrupt: null,
        trace_id: null,
      });
    },
    [applyTurnRecord],
  );

  const cancelTurn = useCallback(async (): Promise<boolean> => {
    const current = pendingTurnRef.current;
    if (!current) return true;
    setPendingTurn({ ...current, status: "cancelling" });
    try {
      let turnId = current.turnId;
      if (!turnId) {
        const latest = await AgentTurnsService.getLatestAgentTurn(current.sessionId);
        if (latest.client_turn_id === current.clientTurnId && latest.status !== "running") {
          applyTurnRecord(latest);
          return true;
        }
        turnId = latest.turn_id;
      }
      await AgentTurnsService.cancelAgentTurn({
        session_id: current.sessionId,
        turn_id: turnId,
      });
      commitAcceptedCancellation(current);
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404 && !current.turnId) {
        // The logical send never reached the server, so explicit Stop can be
        // committed locally without a server turn to cancel.
        commitAcceptedCancellation(current);
        return true;
      }
      if (error instanceof ApiError && error.status === 409) {
        try {
          const latest = await AgentTurnsService.getLatestAgentTurn(current.sessionId);
          if (latest.client_turn_id === current.clientTurnId && latest.status !== "running") {
            applyTurnRecord(latest);
            return true;
          }
        } catch {
          // Fall through and retain the pending turn for the normal poller.
        }
      }
      setPendingTurn((latest) =>
        latest?.clientTurnId === current.clientTurnId
          ? { ...latest, status: current.turnId ? "running" : "reconciling" }
          : latest,
      );
      return false;
    }
  }, [applyTurnRecord, commitAcceptedCancellation, setPendingTurn]);

  return {
    sessionId,
    messages,
    isTyping: showTypingDots || (pendingTurn !== null && !isStreaming),
    isStreaming: isStreaming || pendingTurn !== null,
    isStopping: pendingTurn?.status === "cancelling",
    sendPrompt,
    retryLastError,
    cancelTurn,
  };
};

type ChatSessionContextValue = ReturnType<typeof useChatSessionController>;
const ChatSessionContext = createContext<ChatSessionContextValue | null>(null);

export const ChatSessionProvider = ({ children }: PropsWithChildren) => {
  const value = useChatSessionController();
  return createElement(ChatSessionContext.Provider, { value }, children);
};

export const useChatSession = (): ChatSessionContextValue => {
  const value = useContext(ChatSessionContext);
  if (!value) throw new Error("useChatSession must be used within ChatSessionProvider");
  return value;
};
